import { useCallback, useMemo } from 'react';

import { useSessionStorage } from '~/hooks/useSessionStorage';

const STORAGE_KEY_PREFIX = 'promo-feature-opt-ins';

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

/**
 * Session-scoped, per-user opt-ins for capabilities that their feature entry
 * explicitly marks as opt-in. Callers still enforce the feature's role.
 */
export function usePromoFeatureOptIns(userId: string, optInFeatureKeys: ReadonlySet<string>) {
  const [storedFeatureKeys, setStoredFeatureKeys] = useSessionStorage<Array<string>>(getStorageKey(userId), []);
  const optedInFeatureKeys = useMemo(
    () => new Set(storedFeatureKeys.filter((featureKey) => optInFeatureKeys.has(featureKey))),
    [optInFeatureKeys, storedFeatureKeys],
  );

  const setOptedIn = useCallback(
    (featureKey: string, optedIn: boolean) => {
      if (!optInFeatureKeys.has(featureKey)) {
        return;
      }

      setStoredFeatureKeys((currentFeatureKeys) => {
        const current = Array.isArray(currentFeatureKeys) ? currentFeatureKeys : [];
        const withoutFeature = current.filter((currentFeatureKey) => currentFeatureKey !== featureKey);

        return optedIn ? [...withoutFeature, featureKey] : withoutFeature;
      });
    },
    [optInFeatureKeys, setStoredFeatureKeys],
  );

  return { optedInFeatureKeys, setOptedIn };
}

export function usePromoFeatureOptIn(userId: string, featureKey: string, canOptIn: boolean) {
  const optInFeatureKeys = useMemo(
    () => (canOptIn ? new Set([featureKey]) : new Set<string>()),
    [canOptIn, featureKey],
  );
  const { optedInFeatureKeys, setOptedIn } = usePromoFeatureOptIns(userId, optInFeatureKeys);

  return {
    isOptedIn: optedInFeatureKeys.has(featureKey),
    optOut: useCallback(() => setOptedIn(featureKey, false), [featureKey, setOptedIn]),
  };
}
